import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@store/index';
import {
  login,
  clearError,
  selectAuthLoading,
  selectAuthError,
  selectAuthErrorCode,
  selectIsAuthenticated,
} from '@features/auth/authSlice';
import { clearSessionNotifications } from '@features/ui/uiSlice';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { useRetranslateErrors } from '@/lib/forms/useRetranslateErrors';
import { createLoginSchema, type LoginFormValues } from '../schemas/login.schema';

/**
 * Login form controller: RHF + zod validation wired to the existing Redux auth
 * flow. Preserves the legacy LoginPage behaviour — clears stale errors/session
 * notifications on mount, redirects once authenticated, and clears session
 * notifications again on submit before dispatching the `login` thunk.
 */
export function useLoginForm() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const errorCode = useAppSelector(selectAuthErrorCode);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const schema = useMemo(() => createLoginSchema(t), [t]);
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      tenant_code: '',
      email: '',
      password: '',
      remember_me: false,
    },
  });

  useRetranslateErrors(form);

  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ??
    '/dashboard';

  useEffect(() => {
    dispatch(clearError());
    dispatch(clearSessionNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const onSubmit = form.handleSubmit((values) => {
    dispatch(clearSessionNotifications());
    dispatch(login(values));
  });

  return {
    form,
    onSubmit,
    isLoading,
    error: error
      ? resolveApiErrorMessage(
          t,
          { code: errorCode, message: error },
          { fallbackKey: 'auth.loginError' },
        )
      : error,
    goToForgotPassword: () => navigate('/forgot-password'),
  };
}
